// src/services/NotificationService.js
// Portal-side helper to send push notifications
// Import and call these from any LMS portal page

import { supabase } from '../lib/supabase';

const EDGE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-lms-notification`;

// ── Core sender ───────────────────────────────────────
async function sendNotification({ institution_id, recipient_ids, title, body, data = {}, notification_type = 'general', source_id }) {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const res = await fetch(EDGE_FUNCTION_URL, {
      method:  'POST',
      headers: { 'Content-Type':'application/json', 'Authorization':`Bearer ${token}` },
      body: JSON.stringify({ institution_id, recipient_ids, title, body, data, notification_type, source_id }),
    });
    return await res.json();
  } catch(e) {
    console.error('Notification send failed:', e);
    return { error: e.message };
  }
}

// ── Pre-built triggers for every LMS event ────────────

// 1. New Assignment posted
export const notifyNewAssignment = (institutionId, assignment) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `📋 New Assignment: ${assignment.subject}`,
    body:              `${assignment.title} — Due ${new Date(assignment.due_date).toLocaleDateString()}`,
    notification_type: 'assignment',
    source_id:         assignment.id,
    data:              { screen:'assignments', assignment_id: assignment.id },
  });

//Grade published / updated
export const notifyGradePublished = (recipientIds, assignment, submission) =>
  sendNotification({
    recipient_ids:     recipientIds,
    title:              `⭐ Grade Published: ${assignment.title}`,
    body:               `You received ${submission.marks_obtained}/${assignment.total_marks} marks${submission.grade ? ` (${submission.grade})` : ''}. Tap to view.`,
    notification_type: 'assignment',
    source_id:          submission.id,
    data:               {
      screen: 'assignments',
      assignment_id: assignment.id,
      submission_id: submission.id,
    },
  });

// 2. Assignment deadline reminder
export const notifyAssignmentDue = (institutionId, assignment, recipientIds) =>
  sendNotification({
    institution_id:    institutionId,
    recipient_ids:     recipientIds,
    title:             `⏰ Assignment Due Soon: ${assignment.subject}`,
    body:              `"${assignment.title}" is due ${new Date(assignment.due_date).toLocaleDateString()}. Submit now!`,
    notification_type: 'assignment',
    source_id:         assignment.id,
    data:              { screen:'assignments', assignment_id: assignment.id },
  });

// 3. Results published
export const notifyResultsPublished = (institutionId, term) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `📊 Results Published: ${term.name}`,
    body:              `Your results for ${term.name} are now available. Tap to view your result card.`,
    notification_type: 'result',
    source_id:         term.id,
    data:              { screen:'exams', term_id: term.id },
  });

// 4. New announcement / feed post
export const notifyAnnouncement = (institutionId, announcement) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `📢 ${announcement.title}`,
    body:              announcement.content?.slice(0, 100) || 'New announcement from your institution',
    notification_type: 'announcement',
    source_id:         announcement.id,
    data:              { screen:'feed', announcement_id: announcement.id },
  });

// 5. New event
export const notifyNewEvent = (institutionId, event) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `🎉 New Event: ${event.title}`,
    body:              `${new Date(event.event_date).toLocaleDateString()} — ${event.venue || 'Check details'}`,
    notification_type: 'event',
    source_id:         event.id,
    data:              { screen:'events', event_id: event.id },
  });

// 6. New event created — notify students
export const notifyEventCreated = (institutionId, event) => {
  const eventDate = new Date(event.event_date);
  const now = new Date();

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const startOfEventDay = new Date(
    eventDate.getFullYear(),
    eventDate.getMonth(),
    eventDate.getDate()
  );

  const diffMs = startOfEventDay - startOfToday;
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  let timingText;

  if (daysLeft <= 0) {
    timingText = 'Today';
  } else if (daysLeft === 1) {
    timingText = 'Tomorrow';
  } else if (daysLeft <= 7) {
    timingText = `In ${daysLeft} days`;
  } else {
    timingText = `In ${daysLeft} days`;
  }

  const dateText = eventDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const timeText = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const locationText = event.venue
    ? ` • ${event.venue}`
    : '';

  return sendNotification({
    institution_id: institutionId,
    title: `${event.avatar_emoji || '🎉'} New Event: ${event.title}`,
    body: `${timingText} • ${dateText} at ${timeText}${locationText}`,
    notification_type: 'event',
    source_id: event.id,
    data: {
      screen: 'events',
      event_id: event.id,
      event_date: event.event_date,
      title: event.title,
      category: event.category || 'general',
      venue: event.venue || '',
      organizer: event.organizer || '',
      priority: event.priority || 'normal',
    },
  });
};

// 6. Exam schedule / datesheet published
export const notifyDatesheet = (institutionId, term) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `📅 Exam Datesheet: ${term.name}`,
    body:              `Exams from ${term.start_date} to ${term.end_date}. View full datesheet now.`,
    notification_type: 'exam',
    source_id:         term.id,
    data:              { screen:'exams', term_id: term.id },
  });

// 7. Fee voucher generated
export const notifyFeeVoucher = (recipientIds, voucher) =>
  sendNotification({
    recipient_ids:     recipientIds,
    title:             `💰 Fee Due: Rs ${Number(voucher.total_amount).toLocaleString()}`,
    body:              `${voucher.fee_structure_name} — Due ${voucher.due_date}. Tap to pay via KuickPay.`,
    notification_type: 'fee',
    source_id:         voucher.id,
    data:              { screen:'fees', voucher_id: voucher.id },
  });

// 8. Fee overdue reminder
export const notifyFeeOverdue = (recipientIds, voucher) =>
  sendNotification({
    recipient_ids:     recipientIds,
    title:             `⚠️ Fee Overdue: Rs ${Number(voucher.total_amount).toLocaleString()}`,
    body:              `Your ${voucher.fee_structure_name} fee was due ${voucher.due_date}. Pay now to avoid more fines.`,
    notification_type: 'fee',
    source_id:         voucher.id,
    data:              { screen:'fees', voucher_id: voucher.id },
  });

// 9. New course uploaded
export const notifyCourse = (institutionId, course) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `📚 New Course: ${course.subject}`,
    body:              `${course.title} by ${course.teacher_name || 'Your institution'} is now available.`,
    notification_type: 'course',
    source_id:         course.id,
    data:              { screen:'courses', course_id: course.id },
  });

// 10. New material added to course
export const notifyCourseMaterial = (institutionId, material, courseName) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `📄 New Material: ${courseName}`,
    body:              `"${material.title}" has been added. Tap to view.`,
    notification_type: 'course',
    source_id:         material.id,
    data:              { screen:'courses', material_id: material.id },
  });

// 11. Attendance marked (low attendance warning)
export const notifyLowAttendance = (recipientIds, studentName, percentage) =>
  sendNotification({
    recipient_ids:     recipientIds,
    title:             `⚠️ Low Attendance Warning`,
    body:              `${studentName}, your attendance is ${percentage}%. Minimum required is 75%.`,
    notification_type: 'attendance',
    data:              { screen:'attendance' },
  });

// 12. Book overdue (Library)
export const notifyBookOverdue = (recipientIds, bookTitle, daysOverdue) =>
  sendNotification({
    recipient_ids:     recipientIds,
    title:             `📖 Book Overdue: ${bookTitle}`,
    body:              `This book is ${daysOverdue} day${daysOverdue!==1?'s':''} overdue. Please return it immediately.`,
    notification_type: 'library',
    data:              { screen:'library' },
  });

// 13. Timetable updated
export const notifyTimetableUpdate = (institutionId, className) =>
  sendNotification({
    institution_id:    institutionId,
    title:             `📅 Timetable Updated: ${className}`,
    body:              'Your class timetable has been updated. Check the latest schedule.',
    notification_type: 'timetable',
    data:              { screen:'timetable' },
  });

// 14. Send to specific students (custom)
export const notifyCustom = (recipientIds, title, body, data = {}) =>
  sendNotification({
    recipient_ids:     recipientIds,
    title,
    body,
    notification_type: 'general',
    data,
  });

export default sendNotification;